import React, { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import Appearance from 'styles/Appearance.js';
import TextField from 'views/TextField.js';
import Utils from 'files/Utils.js';
import Views from 'views/Main.js';

const AddressLookupField = ({ distance, icon, inline, insetLabel, loading, onChange, onRenderResult, placeholder, prependComponent, preserveResult, showValidation, style, utils, value }) => {

    const [distanceText, setDistanceText] = useState(0);
    const [isLoading, setLoading] = useState(false);
    const [nonce, setNonce] = useState(uuidv4());
    const [results, setResults] = useState([]);
    const [selected, setSelected] = useState(false);
    const [textTimeout, setTextTimeout] = useState(false);
    const [text, setText] = useState('');

    const onDistanceTextChange = text => {
        let val = parseFloat(text);
        setDistanceText(val);
        if(typeof(onChange) === 'function') {
            onChange({
                ...selected,
                distance: val
            });
        }
    }

    const onRemoveSelectedLocation = () => {
        setText('');
        setSelected(null);
        if(typeof(onChange) === 'function') {
            onChange(null);
        }
    }

    const onResultClick = async place => {
        try {

            // hide results list, update selected place, and update text field label
            setResults([]);
            setSelected(place);
            setText(place.address);

            // remove result text from text field and update selected value if applicable
            if(preserveResult === false) {
                setText('');
                setSelected(null);
            }

            // send geocoding request to server for location coordinate
            setLoading(true);
            let result = await Utils.geocode(utils, place);

            // google cloud requires that a session be reset when a selection has been made
            // session nonce helps batch together autocomplete requests
            setLoading(false);
            setNonce(uuidv4());

            // declare formatted result
            let props = {
                id: place.place_id,
                ...result
            }

            // send formatted place back to subscribers
            if(typeof(onChange) === 'function') {
                if(distance) {
                    onChange({
                        ...props,
                        distance: isNaN(distanceText) ? 0 : distanceText
                    });
                    return;
                }
                onChange(props);
            }

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue preparing the information for the selected location. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onTextChange = text => {

        // clear pending search text timeout if applicable
        if(textTimeout) {
            clearTimeout(textTimeout);
        }

        // update textfield label, remove previously selected result, and begin request loading
        setText(text);
        setLoading(true);
        setSelected(null);

        // prepare next search text timeout
        setTextTimeout(async () => {
            try {

                // prevent moving forward if search text length is less than three characters
                await Utils.sleep(0.25);
                if(!text || text.length < 3) {
                    setResults([]);
                    setLoading(false);
                    return;
                }

                // send auto complete request to server
                let { results } = await Utils.addressLookup(utils, text, nonce);
                setLoading(false);
                setResults(results || []);

            } catch(e) {
                setLoading(false);
                utils.alert.show({
                    title: 'Oops!',
                    message: `There was an issue completing your search. ${e.message || 'An unknown error occurred'}`
                });
            }

        });
    }

    const onUpdateValue = () => {

        // prevent moving forward if no value was provided
        if(!value) {
            setSelected(null);
            setText(null);
            return;
        }

        // check if value represents a place and a distance
        if(distance) {
            setDistanceText(parseFloat(value.distance));
            setSelected(value);
            setText(Utils.format.address(value.place));
            return;
        }

        // fallback to setting values using the default model for places
        setSelected(value);
        setText(Utils.format.address(value));
    }

    const onValidate = () => {
        return selected ? true : false
    }

    const getStyles = () => {
        if(inline !== true) {
            return {
                ...Appearance.styles.unstyledPanel(),
                overflow: 'hidden',
                marginTop: 8,
                ...insetLabel && {
                    borderRadius: 8
                }
            }
        }
        return Views.floating();
    }

    useEffect(() => {
        setLoading(loading);
    }, [loading]);

    useEffect(() => {
        onUpdateValue();
    }, [value]);

    return (
        <div style={{
            position: 'relative',
            textAlign: 'left',
            width: '100%',
            ...style,
            height: 'auto'
        }}>
            <div style={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'row',
                width: '100%'
            }}>
                <TextField
                appendComponent={selected && Views.icon.remove({ onClick: onRemoveSelectedLocation })}
                autoComplete={false}
                autoCorrect={false}
                autoCapitalize={'sentences'}
                insetLabel={insetLabel}
                icon={icon || 'location'}
                loading={isLoading}
                onChange={onTextChange}
                placeholder={insetLabel ? null : (placeholder || 'Search by name or address...')}
                prependComponent={prependComponent}
                useDelay={true}
                value={text}
                containerStyle={{ 
                    flexGrow: 1
                }} />

                {typeof(distance) === 'object' && (
                    <TextField
                    {...distance}
                    format={'number'}
                    onChange={onDistanceTextChange}
                    value={distanceText}
                    containerStyle={{
                        marginLeft: 8,
                        width: '25%',
                        minWidth: 125
                    }}/>
                )}
            </div>
            {results.length > 0 && (
                <div style={getStyles()}>
                    {results.map((location, index) => {
                        if(typeof(onRenderResult) === 'function') {
                            return onRenderResult(location, index, results);
                        }
                        return (
                            Views.entry({
                                key: index,
                                title: location.name,
                                subTitle: location.address,
                                hideIcon: true,
                                onClick: onResultClick.bind(this, location),
                                bottomBorder: index !== results.length - 1
                            })
                        )
                    })}
                </div>
            )}
        </div>
    )
}
export default AddressLookupField;
